import { RootState } from "index";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import { Row, Col, Label, Card, Button, Input, Container, CardBody } from "reactstrap";
import { Get } from "VesselMaster/vesselMaster.hooks";
import { getVesselsAction, setOnboardedVessels, setVesselID } from "Store/Generic/ReportingSlice";
import { useQuery } from "react-query";
import { queryKeyes } from "shared/queryKeys";
import { customStyle } from "shared/CommonCSS";
import FormValuesDebug from "utils/debugTools/FormValuesDebug";
import * as Yup from "yup";
import ErrorTooltip from "Components/ErrorTooltip";
import { useFormik } from "formik";
import apiGlobal from "global/api.global";
import { errorToast, successToast } from "Components/Toasts";
import Message from "Components/Message";
import { useDocumentTitle } from "Components/useDocument.hooks";
import { OnboardingStatus } from "shared/constants";
import { dateTimeFormat } from "GenericForms/Helper";
import { commonValidationMessages } from "Components/ValidationErrorMessages";
import { EuaFiltervalues } from "shared/CommonInterface";
import { queryClient } from "react-query/queryClient";
import env from "environment_system/env_system";
import DateRangePopup from "ReportGeneration/DateRangePopup";
import Layout from "HorizontalMenu/Menu";
import Navbar from "HorizontalMenu/Navbar";
import EuaVoyageTable from "./EuaVoyageTable";
interface ReportGenerationFiltersType {
    isPopupOpen?: boolean;
    setisPopupOpen?: (isPopupOpen: boolean) => void;
}

const EuMrvGenerationFilters = ({ isPopupOpen = false, setisPopupOpen }: ReportGenerationFiltersType) => {
    /** State variables */
    const dispatch = useDispatch();
    const { Vessels, VesselID, onboardedVessels } = useSelector(
        (state: RootState) => state.Reporting
    );
    const [refreshKey, setRefreshKey] = useState(0);
    const [selectedRadio, setSelectedRadio] = useState("year");
    const vessel = Vessels.find((rec: any) => rec.id === VesselID);
    const [dateRangePopup, setDateRangePopup] = useState(false);
    const [minDate, setMinDate] = useState<any>("");
    const [maxDate, setMaxDate] = useState<any>("");
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const setFilterValues = (values: EuaFiltervalues) => { }; // Set the filter values
    useDocumentTitle('Custom Report - ecoSAIL');

    /** Queries */
    const { data: vesselYears, isLoading: vesselYearsLoading } = useQuery(
        [queryKeyes.vessel.VesselYear.key, VesselID],
        async () => await Get(queryKeyes.vessel.VesselYear.url(VesselID), VesselID),
    );
    /**end of Queries */
    /** Handle vessel selection */
    const handleVesselSelectionChanges = (e: any) => {
        dispatch(setVesselID(e ? e?.id : null));
    };
    const validationSchema = Yup.object().shape({
        year: Yup.string().nullable(),
        startDate: Yup.string().nullable().test(
            "start-date-validation",
            "Start date should be less than end date",
            function (startDate: any) {
                const { endDate } = this.parent;
                const startDT = new Date(startDate);
                const endDT = new Date(endDate);
                const minDT = new Date(minDate);
                const maxDT = new Date(maxDate);
                if (startDT && endDT) {
                    if (startDT < minDT && startDate !== null) {
                        return new Yup.ValidationError(`Start date should be same or after ${minDate}`, undefined, `startDate`);
                    }
                    if (startDT >= maxDT) {
                        return new Yup.ValidationError(`Start date should be before ${maxDate}`, undefined, `startDate`);
                    }
                    if (endDT > maxDT) {
                        return new Yup.ValidationError(`End date should be less than ${maxDate}`, undefined, `endDate`);
                    }
                    if (startDT > endDT && endDate !== null) {
                        return new Yup.ValidationError("End date should be after start date", undefined, `endDate`);
                    }
                }
                return true;
            }
        ),
        endDate: Yup.string().nullable(),
        startReport: Yup.number()
            .nullable()
            .when('selectedRadio', {
                is: 'startDate',
                then: schema => schema.required(commonValidationMessages.required),
                otherwise: schema => schema.nullable(),
            }),
        endReport: Yup.number()
            .nullable()
            .when('selectedRadio', {
                is: 'startDate',
                then: schema => schema.required(commonValidationMessages.required),
                otherwise: schema => schema.nullable(),
            }),
        emails: Yup.array().of(
            Yup.string().email('Invalid email format')
        ),
        selectedRadio: Yup.string().nullable().test(
            "at-least-one",
            "Please select any one report parameters",
            function () {
                const { year, startDate, } = this.parent;
                // Early returns for better performance
                if (year) return true;
                if (startDate && startDate.length > 0) return true;
                return false;
            }
        ),
    });

    /** useEffect */
    /** Populate Vessels array */
    useEffect(() => {
        dispatch(getVesselsAction("vessel_master" as string));
    }, [dispatch]);

    useEffect(() => {
        apiGlobal.get(queryKeyes.vessel.GetMinMaxReportDate.url(VesselID)).then(response => {
            setMinDate(response.data?.start_date);
            setMaxDate(response.data?.end_date);
        }).catch(err => {
            console.error(err);
        })
    }, [VesselID])

    /** Effect 2: Set onboarded vessels when Vessels data is available */
    useEffect(() => {
        if (Array.isArray(Vessels)) {
            const onboardedVessels = Vessels.filter(
                (vessel) => vessel.onboarding_status === OnboardingStatus.ONBOARDED
            );
            dispatch(setOnboardedVessels(onboardedVessels));
        }
    }, [Vessels, dispatch]);
    /** useEffect end */

    /** Formik Setup */
    const euaReportUseFormik = useFormik({
        enableReinitialize: true,
        initialValues: {
            vessel_id: VesselID,
            year: null as string,
            startDate: null as string,
            endDate: null as string,
            startReport: null as number,
            endReport: null as number,
            selectedRadio: null as string,
            emails: [''],
        },
        validationSchema: validationSchema,
        onSubmit: (values: any, actions: any) => {
            actions.setSubmitting(true);
            const selectedValue = selectedRadio;
            let requestBody: any = {
                vessel_id: values?.vessel_id,
            };
            if (selectedValue === "year") {
                requestBody.year = values.year;
            } else if (selectedValue === "startDate") {
                requestBody.start_report_id = values.startReport;
                requestBody.end_report_id = values.endReport;
            }
            // Make API call
            apiGlobal.post(`/excel_download/`, requestBody)
                .then(res => {
                    if (res.status === 200) {
                        successToast("Report generated successfully!");
                    }
                })
                .catch(err => {
                    errorToast(err?.response?.data?.error || "Error generating report!");
                }).finally(() => {
                    setDateRangePopup(false);
                    actions.setSubmitting(false);
                });
        },
    });

    const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const selectedValue = event.target.id;
        setSelectedRadio(selectedValue);
        euaReportUseFormik.setValues({
            ...euaReportUseFormik.values,
            year: selectedValue === "year" ? euaReportUseFormik.values.year : null,
            startDate: selectedValue === "startDate" ? euaReportUseFormik.values.startDate : null,
            endDate: selectedValue === "endDate" ? euaReportUseFormik.values.endDate : null,
        });
        setRefreshKey(refreshKey + 1);
    };
    useEffect(() => {
        if (!dateRangePopup) {
            const hasErrors = euaReportUseFormik?.errors && Object.keys(euaReportUseFormik.errors).length > 0;
            if (hasErrors) {
                euaReportUseFormik?.setValues({
                    ...euaReportUseFormik.values,
                    startDate: null as string,
                    endDate: null as string,
                    startReport: null as number,
                    endReport: null as number,
                });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dateRangePopup]);
    return (
        <React.Fragment>
            <Layout>
                <Navbar />
            </Layout>
            <div className="page-content">
                <Container fluid>
                    <Card className="p-2">
                        <form
                            onSubmit={euaReportUseFormik.handleSubmit}
                            noValidate
                            autoComplete="off"
                        >
                            {!isPopupOpen && (
                                <Row className="mb-3">
                                    <Col sm={4}>
                                        <Label className="mb-1">Select vessel</Label>
                                        <Select
                                            options={onboardedVessels as any[]}
                                            id="vessel_id"
                                            getOptionLabel={(option: any) => {
                                                const vesselName = option.vessel_name;
                                                const vesselType = option.vessel_type
                                                    ? `(${option.vessel_type_name})`
                                                    : "";
                                                return `${vesselName} ${vesselType}`;
                                            }}
                                            getOptionValue={(option: any) => option.id}
                                            onChange={(e: any) => {
                                                euaReportUseFormik.setFieldValue("year", null);
                                                euaReportUseFormik.setFieldValue("startDate", null);
                                                euaReportUseFormik.setFieldValue("vessel_id", e?.id);
                                                handleVesselSelectionChanges(e);
                                                setRefreshKey(refreshKey + 1);
                                            }}
                                            isClearable={true}
                                            menuPortalTarget={document.body}
                                            styles={customStyle}
                                            value={
                                                VesselID
                                                    ? { id: VesselID, vessel_name: vessel?.vessel_name }
                                                    : null
                                            }
                                        />
                                    </Col>
                                    {euaReportUseFormik.errors?.vessel_id && (
                                        <ErrorTooltip
                                            target={`vessel_id`}
                                            message={euaReportUseFormik.errors?.vessel_id}
                                            open={euaReportUseFormik.errors?.vessel_id}
                                        />
                                    )}
                                </Row>
                            )}
                            {!VesselID && (
                                <Message message="Please select a vessel to proceed" type="warning" width="365px" />
                            )}
                            <div className="mt-3">
                                <React.Fragment>
                                    {!isPopupOpen &&
                                        <React.Fragment>
                                            <h6> Enter Emails </h6>
                                            <Row>
                                                {euaReportUseFormik.values.emails.map((email: string, index: number) => (
                                                    <Row key={index} className="mb-2 align-items-end">
                                                        <Col lg={3}>
                                                            <Label htmlFor={`emails_${index}`} className="mb-1">
                                                                Email {index + 1}
                                                            </Label>
                                                            <Input
                                                                type="email"
                                                                id={`emails_${index}`}
                                                                name={`emails.${index}`}
                                                                value={email}
                                                                placeholder="Enter email address"
                                                                autoComplete="off"
                                                                onChange={euaReportUseFormik.handleChange}
                                                                onBlur={() =>
                                                                    euaReportUseFormik.setFieldTouched(`emails.${index}`, true)
                                                                } // mark individual field as touched
                                                                className={`form-control`}
                                                                disabled={!VesselID}
                                                            />

                                                            {/* Error Tooltip with touch check */}
                                                            {Array.isArray(euaReportUseFormik.errors.emails) &&
                                                                Array.isArray(euaReportUseFormik.touched.emails) &&
                                                                env.form_validation &&
                                                                euaReportUseFormik.errors.emails[index] &&
                                                                euaReportUseFormik.touched.emails[index] && (
                                                                    <ErrorTooltip
                                                                        target={`emails_${index}`}
                                                                        message={euaReportUseFormik.errors.emails[index]}
                                                                        open={!!euaReportUseFormik.errors.emails[index]}
                                                                    />
                                                                )}
                                                        </Col>
                                                        <Col lg={2}>
                                                            <div className="ele_row1">
                                                                {euaReportUseFormik.values.emails.length > 1 && (
                                                                    <button
                                                                        type="button"
                                                                        className="btn"
                                                                        onClick={() => {
                                                                            const newEmails = [...euaReportUseFormik.values.emails];
                                                                            newEmails.splice(index, 1);
                                                                            euaReportUseFormik.setFieldValue('emails', newEmails);
                                                                        }}
                                                                    >
                                                                        <i className="dripicons-trash icon_s18" />
                                                                    </button>
                                                                )}

                                                                {index === euaReportUseFormik.values.emails.length - 1 && euaReportUseFormik.values.emails.length <= 9 && (
                                                                    <Button
                                                                        type="button"
                                                                        className="btn ms-1"
                                                                        color="primary"
                                                                        onClick={() =>
                                                                            euaReportUseFormik.setFieldValue('emails', [
                                                                                ...euaReportUseFormik.values.emails,
                                                                                '',
                                                                            ])
                                                                        }
                                                                    >
                                                                        <i className="dripicons-plus icon_s18 navbar_menu" />
                                                                    </Button>
                                                                )}
                                                            </div>
                                                        </Col>
                                                    </Row>
                                                ))}
                                            </Row>
                                        </React.Fragment>
                                    }
                                    <Row className="mb-4">
                                        <h6 className="mb-2">Select report parameters</h6>
                                        <Col sm={6}>
                                            <div className="ele_row1">
                                                <input
                                                    className="form-check-input"
                                                    type="radio"
                                                    name="reportParameter"
                                                    id="year"
                                                    disabled={!VesselID}
                                                    checked={selectedRadio === "year"}
                                                    onChange={(e) => {
                                                        handleRadioChange(e);
                                                        euaReportUseFormik.setFieldValue("selectedRadio", "year");
                                                        setDateRangePopup(false);
                                                    }}
                                                    onClick={() => {
                                                        euaReportUseFormik.setFieldValue("selectedRadio", "year")
                                                    }}
                                                />
                                                <div className="w-70">
                                                    <Label className="mb-1 ms-2" htmlFor="year">
                                                        Select year
                                                    </Label>
                                                    <Select
                                                        key={refreshKey}
                                                        options={vesselYears}
                                                        getOptionLabel={(option: any) => option.year}
                                                        getOptionValue={(option: any) => option.year}
                                                        onChange={(option: any) => {
                                                            euaReportUseFormik.setFieldValue("year", option.year);
                                                            setDateRangePopup(false);
                                                        }}
                                                        styles={customStyle}
                                                        isDisabled={selectedRadio !== "year" || !VesselID}
                                                        isLoading={vesselYearsLoading}
                                                    />
                                                </div>
                                            </div>
                                        </Col>
                                        <Col sm={6}>
                                            <div className="ele_row1">
                                                <input
                                                    className="form-check-input"
                                                    type="radio"
                                                    name="reportParameter"
                                                    id="startDate"
                                                    disabled={!VesselID}
                                                    checked={selectedRadio === "startDate"}
                                                    onChange={(e) => {
                                                        handleRadioChange(e);
                                                        euaReportUseFormik.setFieldValue("selectedRadio", "startDate");
                                                    }}
                                                    onClick={() => {
                                                        euaReportUseFormik.setFieldValue("selectedRadio", "startDate");
                                                        setDateRangePopup(true);
                                                    }}
                                                />
                                                <div className="w-70">
                                                    <Label className="mb-1 ms-2" htmlFor="startDate">
                                                        Select date range
                                                    </Label>
                                                    <DateRangePopup
                                                        dateRangePopup={dateRangePopup}
                                                        setDateRangePopup={setDateRangePopup}
                                                        customReportUseFormik={euaReportUseFormik}
                                                        minDate={minDate}
                                                        maxDate={maxDate}
                                                        setFilterValues={setFilterValues}
                                                        setisPopupOpen={setisPopupOpen}
                                                        isPopupOpen={isPopupOpen}
                                                    /><br />
                                                    <Label className="mb-0">Start date:
                                                        <strong className="ms-2">
                                                            {dateTimeFormat(euaReportUseFormik?.values?.startDate, false, false, true) ?? '-'}
                                                        </strong>
                                                    </Label><br />
                                                    <Label className="mb-0" id="endDate_lbl">End date:
                                                        <strong style={{ marginLeft: '0.75rem' }}>
                                                            {dateTimeFormat(euaReportUseFormik?.values?.endDate, false, false, true) ?? '-'}
                                                        </strong>
                                                    </Label>
                                                </div>
                                            </div>
                                        </Col>
                                        <Col sm={12} className="mt-4">
                                            {euaReportUseFormik?.errors?.selectedRadio && (
                                                <Message message={euaReportUseFormik?.errors?.selectedRadio} type="danger" width="365px" />
                                            )}
                                        </Col>
                                    </Row>

                                    <Row className="mb-2 justify-content-between">
                                        <Col sm={10} className="d-flex justify-content-start align-items-center ">
                                        </Col>
                                        {!isPopupOpen ? (
                                            <Col sm={2} className="d-flex justify-content-end ">
                                                <Button color="primary"
                                                    type="submit"
                                                    disabled={euaReportUseFormik.isSubmitting || !VesselID}
                                                    className={`${euaReportUseFormik.isSubmitting && 'disabled'}`}>
                                                    {euaReportUseFormik.isSubmitting ? (
                                                        <>
                                                            <i className="spinner-border spinner-border-sm text-light me-2" />
                                                            Generating...
                                                        </>
                                                    ) : (
                                                        "Apply"
                                                    )}
                                                </Button>
                                            </Col>
                                        ) : (
                                            <Col className="d-flex justify-content-end">
                                                <Button
                                                    color="primary"
                                                    type="button"
                                                    disabled={!euaReportUseFormik?.isValid || !VesselID}
                                                    onClick={() => {
                                                        if (euaReportUseFormik?.isValid) {
                                                            setFilterValues(euaReportUseFormik.values);
                                                            setisPopupOpen(false);
                                                            queryClient.invalidateQueries(queryKeyes.vessel.CIIAssessmentData.key);
                                                        }
                                                    }}
                                                >
                                                    Apply
                                                </Button>
                                            </Col>
                                        )}
                                    </Row>
                                </React.Fragment>
                            </div>
                            {/* <FormValuesDebug
                                values={[
                                    euaReportUseFormik?.values,
                                    euaReportUseFormik?.errors,
                                    euaReportUseFormik?.touched,
                                ]}
                            /> */}
                        </form>
                    </Card>
                    <Card className="p-0">
                        <CardBody className="p-2">
                            <EuaVoyageTable />
                        </CardBody>
                    </Card>
                </Container>
            </div>
            <FormValuesDebug values={[euaReportUseFormik?.values, euaReportUseFormik?.errors, euaReportUseFormik?.touched]} />
        </React.Fragment>
    );
};

export default EuMrvGenerationFilters;
